import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Form, InputNumber, Switch, Avatar, Select } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined, GlobalOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import '../Users/Users.css';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  .ant-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const BlockchainNetworks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [networks, setNetworks] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  // Edit functionality moved to detail page
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageId, perPage]);

  const fetchNetworks = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (search) {
        // Backend may ignore this, but keep for potential future support
        params.search = search;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS,
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        // Handle paginate utility format: { itemsList, paginator: { itemCount, perPage, pageCount, currentPage } }
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setNetworks(items);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              0,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: items.length,
          }));
        }
      }
    } catch (error: any) {
      message.error('Failed to fetch blockchain networks');
      console.error('Error fetching networks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setModalVisible(true);
  };


  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };


  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Remove fields that shouldn't be sent in create
      const { blockchainType, isCongested, asorockAcknowledged, ...createPayload } = values;

      // Only create, edit is done on detail page
      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_BLOCKCHAIN_NETWORK, createPayload);
      message.success('Blockchain network created successfully');
      
      setModalVisible(false);
      fetchNetworks();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save blockchain network');
    }
  };


  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {record.iconUrl ? (
            <div
              style={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 4,
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.bgSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              <img
                src={record.iconUrl}
                alt={record.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: '4px'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.innerHTML = '<GlobalOutlined />';
                }}
              />
            </div>
          ) : (
            <Avatar
              icon={<GlobalOutlined />}
              size={32}
              shape="square"
              style={{ 
                backgroundColor: COLORS.bgSecondary, 
                color: COLORS.textPrimary, 
                border: `1px solid ${COLORS.border}`,
                flexShrink: 0
              }}
            />
          )}
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            {record.abbreviation && (
              <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                {record.abbreviation}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Blockchain Type',
      dataIndex: 'blockchainType',
      key: 'blockchainType',
      render: (type: string) => <Tag color="cyan">{type}</Tag>,
    },
    {
      title: 'Network ID',
      dataIndex: 'networkId',
      key: 'networkId',
      render: (id: number) => id ?? '-',
    },
    {
      title: 'Is Congested',
      dataIndex: 'isCongested',
      key: 'isCongested',
      render: (isCongested: boolean) => <Tag color={isCongested ? 'orange' : 'default'}>{isCongested ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'AsoRock Ack',
      dataIndex: 'asorockAcknowledged',
      key: 'asorockAcknowledged',
      render: (acknowledged: boolean) => <Tag color={acknowledged ? 'green' : 'red'}>{acknowledged ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Allow in Watcher',
      dataIndex: 'allowInOnchainWatcherMonitor',
      key: 'allowInOnchainWatcherMonitor',
      render: (value: boolean) => (
        <Tag color={value !== false ? 'green' : 'red'}>
          {value !== false ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Supports RPC Polling',
      dataIndex: 'supportsRPCPolling',
      key: 'supportsRPCPolling',
      render: (value: boolean) => (
        <Tag color={value !== false ? 'green' : 'red'}>
          {value !== false ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Trace Block Enabled',
      dataIndex: 'traceBlockEnabled',
      key: 'traceBlockEnabled',
      render: (value: boolean) => (
        <Tag color={value !== false ? 'green' : 'red'}>
          {value !== false ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/kivu/blockchain-networks/${record.id}`)}
            size="small"
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Blockchain Networks
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add Network
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
              Search
            </Text>
            <Input
              placeholder="Search by name or abbreviation"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => {
                const newParams = new URLSearchParams(searchParams);
                if (search) {
                  newParams.set('search', search);
                } else {
                  newParams.delete('search');
                }
                newParams.set('pageId', '1'); // Reset to first page on search
                setSearchParams(newParams);
              }}
              style={{ maxWidth: 400 }}
            />
          </div>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ marginBottom: 12, color: COLORS.textSecondary }}>
          Total {pagination.total} blockchain networks
        </div>
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={networks}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} networks
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            className="dark-table"
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="Create Blockchain Network"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Create"
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter network name' }]}
          >
            <Input placeholder="e.g., Ethereum Mainnet" />
          </Form.Item>
          <Form.Item
            name="abbreviation"
            label="Abbreviation"
            rules={[{ required: true, message: 'Please enter abbreviation' }]}
          >
            <Input placeholder="e.g., ETH" />
          </Form.Item>
          <Form.Item
            name="networkId"
            label="Network ID"
            rules={[{ required: true, message: 'Please enter network ID' }]}
            dependencies={['blockchainType']}
          >
            {({ getFieldValue }) => {
              const blockchainType = getFieldValue('blockchainType');
              if (blockchainType === 'BITCOIN') {
                return (
                  <Select placeholder="Select network">
                    <Select.Option value={1}>1 - Mainnet</Select.Option>
                    <Select.Option value={0}>0 - Testnet</Select.Option>
                  </Select>
                );
              }
              return <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g., 1" />;
            }}
          </Form.Item>
          <Form.Item
            name="nativeTokenSymbol"
            label="Native Token Symbol"
            rules={[{ required: true, message: 'Please enter native token symbol' }]}
          >
            <Input placeholder="e.g., ETH" />
          </Form.Item>
          <Form.Item
            name="tokenStandard"
            label="Token Standard"
            rules={[{ required: true, message: 'Please enter token standard' }]}
          >
            <Input placeholder="e.g., ERC20" />
          </Form.Item>
          <Form.Item
            name="explorerUrl"
            label="Explorer URL"
          >
            <Input placeholder="https://etherscan.io" />
          </Form.Item>
          <Form.Item
            name="iconUrl"
            label="Icon URL"
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="nativeTokenAggregatorSlug"
            label="Native Token Aggregator Slug"
          >
            <Input placeholder="e.g., ethereum" />
          </Form.Item>
          <Form.Item
            name="allowWithdrawal"
            label="Allow Withdrawal"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="allowDeposit"
            label="Allow Deposit"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="isERC20"
            label="Is ERC20"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="blockchainType"
            label="Blockchain Type"
            initialValue="EVM"
            rules={[{ required: true, message: 'Please select blockchain type' }]}
          >
            <Select>
              <Select.Option value="EVM">EVM</Select.Option>
              <Select.Option value="SOLANA">SOLANA</Select.Option>
              <Select.Option value="TRON">TRON</Select.Option>
              <Select.Option value="TON">TON</Select.Option>
              <Select.Option value="BITCOIN">BITCOIN</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="isCongested"
            label="Is Congested"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">ACTIVE</Select.Option>
              <Select.Option value="PENDING">PENDING</Select.Option>
              <Select.Option value="SUSPENDED">SUSPENDED</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default BlockchainNetworks;

